import React, { useCallback, useEffect, useState } from 'react';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { PageHeader } from '../../../shared/components/PageHeader';
import { Spinner } from '../../../shared/components/Spinner';
import { ErrorAlert } from '../../../shared/components/ErrorAlert';
import { useDocumentTitle } from '../../../shared/hooks/useDocumentTitle';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchCompanyOptions, type CompanyOption } from '../../../services/companies/companyODataApi';
import { fetchAdminCompanyMerchants } from '../../../services/admin/adminCompanyService';
import { inviteMerchant } from '../../../services/merchants/adminMerchantApi';
import { CompanyDirectoryTable } from '../components/CompanyDirectoryTable';
import type { AdminCompanyDirectoryRow } from '../../../models/adminCompany';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Admin "impersonate company": pick a 3PL company, then view/resend/send invitations on its behalf. */
export function CompanyInvitationsPage(): React.ReactElement {
  useDocumentTitle('Company invitations · Triplicity Backoffice');
  const { getAccessToken, acquireAccessToken } = useAuth();

  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [companiesError, setCompaniesError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | undefined>(undefined);

  const [rows, setRows] = useState<AdminCompanyDirectoryRow[]>([]);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [directoryError, setDirectoryError] = useState<string | null>(null);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteFieldErrors, setInviteFieldErrors] = useState<Partial<Record<string, string>>>({});
  const [inviteSubmitError, setInviteSubmitError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);

  useEffect(() => {
    let isMounted = true;
    setCompaniesLoading(true);
    setCompaniesError(null);
    fetchCompanyOptions(acquireAccessToken)
      .then((list) => {
        if (isMounted) setCompanies(list);
      })
      .catch((e) => {
        if (isMounted) {
          setCompanies([]);
          setCompaniesError(e instanceof Error ? e.message : 'Failed to load companies.');
        }
      })
      .finally(() => {
        if (isMounted) setCompaniesLoading(false);
      });
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDirectory = useCallback(async (): Promise<void> => {
    if (!companyId) {
      return;
    }
    setDirectoryLoading(true);
    setDirectoryError(null);
    try {
      const list = await fetchAdminCompanyMerchants(companyId, getAccessToken);
      setRows(list);
    } catch (e) {
      setRows([]);
      setDirectoryError(e instanceof Error ? e.message : 'Failed to load company directory.');
    } finally {
      setDirectoryLoading(false);
    }
  }, [companyId, getAccessToken]);

  useEffect(() => {
    void loadDirectory();
  }, [loadDirectory]);

  const validateInvite = (): boolean => {
    const next: Partial<Record<string, string>> = {};
    const em = inviteEmail.trim();
    if (!em) {
      next.email = 'Email is required.';
    } else if (!EMAIL_RE.test(em)) {
      next.email = 'Enter a valid email address.';
    }
    if (!inviteName.trim()) {
      next.suggestedMerchantName = 'Suggested merchant name is required.';
    }
    setInviteFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleInviteSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setInviteSubmitError(null);
    setInviteSuccess(null);
    if (!companyId || !validateInvite()) {
      return;
    }
    setInviteSubmitting(true);
    try {
      await inviteMerchant(
        {
          email: inviteEmail.trim(),
          suggestedMerchantName: inviteName.trim(),
          companyId,
        },
        acquireAccessToken
      );
      setInviteSuccess('Invitation sent successfully.');
      setInviteEmail('');
      setInviteName('');
      setInviteFieldErrors({});
      await loadDirectory();
    } catch (err) {
      setInviteSubmitError(err instanceof Error ? err.message : 'Invitation failed.');
    } finally {
      setInviteSubmitting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Company invitations"
        subtitle="Select a 3PL company to view and manage its merchant invitations, as that company would."
      />

      <div className="bo-card">
        <div className="bo-field">
          <span className="bo-field-label" id="directory-company-label">
            Company
          </span>
          {companiesLoading ? (
            <Spinner label="Loading companies…" />
          ) : companiesError ? (
            <ErrorAlert message={companiesError} />
          ) : (
            <div className="bo-invite-ddl-wrap">
              <DropDownListComponent
                id="directory-company"
                dataSource={companies as unknown as { [key: string]: Object }[]}
                fields={{ text: 'text', value: 'value' }}
                placeholder="Select company"
                allowFiltering
                filterBarPlaceholder="Search companies"
                popupHeight="240px"
                value={companyId}
                change={(args) => {
                  setCompanyId(args.value != null && args.value !== '' ? String(args.value) : undefined);
                  setInviteSuccess(null);
                  setInviteSubmitError(null);
                }}
                cssClass="bo-invite-company-ddl"
              />
            </div>
          )}
        </div>
      </div>

      {companyId ? (
        <>
          <form className="bo-card bo-form-card" onSubmit={(e) => void handleInviteSubmit(e)}>
            <h2 className="bo-form-section-title">Send new invitation</h2>
            <div className="bo-form-fields">
              <div className="bo-field">
                <label className="bo-field-label" htmlFor="directory-invite-email">
                  Email
                </label>
                <input
                  id="directory-invite-email"
                  className="bo-input"
                  type="email"
                  value={inviteEmail}
                  onChange={(ev) => {
                    setInviteEmail(ev.target.value);
                    setInviteFieldErrors((p) => {
                      const { email: _, ...rest } = p;
                      return rest;
                    });
                  }}
                  autoComplete="email"
                />
                {inviteFieldErrors.email ? (
                  <span className="bo-field-error" role="alert">
                    {inviteFieldErrors.email}
                  </span>
                ) : null}
              </div>
              <div className="bo-field">
                <label className="bo-field-label" htmlFor="directory-invite-name">
                  Suggested merchant name
                </label>
                <input
                  id="directory-invite-name"
                  className="bo-input"
                  type="text"
                  value={inviteName}
                  onChange={(ev) => {
                    setInviteName(ev.target.value);
                    setInviteFieldErrors((p) => {
                      const { suggestedMerchantName: _, ...rest } = p;
                      return rest;
                    });
                  }}
                  autoComplete="organization"
                />
                {inviteFieldErrors.suggestedMerchantName ? (
                  <span className="bo-field-error" role="alert">
                    {inviteFieldErrors.suggestedMerchantName}
                  </span>
                ) : null}
              </div>
            </div>
            {inviteSuccess ? (
              <p className="bo-form-success" role="status">
                {inviteSuccess}
              </p>
            ) : null}
            {inviteSubmitError ? <ErrorAlert message={inviteSubmitError} /> : null}
            <div className="bo-actions" style={{ marginTop: '1.25rem' }}>
              <button type="submit" className="bo-btn bo-btn-primary" disabled={inviteSubmitting}>
                {inviteSubmitting ? 'Sending…' : 'Send invitation'}
              </button>
            </div>
          </form>

          <div className="bo-card">
            <div className="bo-admin-table-head">
              <h2 className="bo-admin-section-title">Merchant directory</h2>
              <p className="bo-muted">Merchants and invitations for this company.</p>
            </div>
            {directoryLoading ? <Spinner label="Loading directory…" /> : null}
            {!directoryLoading && directoryError ? <ErrorAlert message={directoryError} /> : null}
            {!directoryLoading && !directoryError ? (
              <CompanyDirectoryTable rows={rows} companyId={companyId} onResent={() => void loadDirectory()} />
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}
